import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { AppProvider } from "@/contexts/AppContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import HomePage from "@/pages/HomePage";
import ProjectsPage from "@/pages/ProjectsPage";
import TasksPage from "@/pages/TasksPage";
import ExpensesPage from "@/pages/ExpensesPage";
import SharedWithMePage from "@/pages/SharedWithMePage";
import DeletedRecordsPage from "@/pages/DeletedRecordsPage";
import HabitsPage from "@/pages/HabitsPage";
import AssetsPage from "@/pages/AssetsPage";
import VehiclesPage from "@/pages/VehiclesPage";
import RemindersPage from "@/pages/RemindersPage";
import VendorsPage from "@/pages/VendorsPage";
import AdminPage from "@/pages/AdminPage";
import { Loader2 } from "lucide-react";
import { useEffect } from "react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" data-testid="loading-screen">
      <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
    </div>
  );
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
    </div>
  );
  if (!user || !['admin','super_admin'].includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
    </div>
  );
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  useEffect(() => {
    document.title = "Nexus | Making Life Easier";
  }, []);

  return (
    <Routes>
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/" element={<ProtectedRoute><AppProvider><Layout /></AppProvider></ProtectedRoute>}>
        <Route index element={<HomePage />} />
        <Route path="tasks" element={<TasksPage />} />
        <Route path="expenses" element={<ExpensesPage />} />
        <Route path="habits" element={<HabitsPage />} />
        <Route path="vehicles" element={<VehiclesPage />} />
        <Route path="assets" element={<AssetsPage />} />
        <Route path="reminders" element={<RemindersPage />} />
        <Route path="vendors" element={<VendorsPage />} />
        <Route path="workspaces" element={<ProjectsPage />} />
        <Route path="shared" element={<SharedWithMePage />} />
        <Route path="trash" element={<DeletedRecordsPage />} />
        <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="bottom-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
